<section class="impact-brief">
  <!-- ONE flat card holding ONE table, not a card per package holding a plate
       per page holding another plate. Four packages used to be four bordered
       boxes, each with a 3px accent bar down its left edge that distinguished
       nothing (every package painted the same colour), and each page inside
       them was a `--surface` plate with its own radius sitting on a
       `--surface-raised` card. Three nested boxes to render a row.

       Packages are section headers inside the table now, which is what they
       are: a grouping of rows, not a container of lists. -->
  <div class="card impact-sheet" *ngIf="packages.length; else noPackages">
    <!-- The strip's own header, inside the card rather than floating above it,
         so the section has one boundary instead of a heading, a paragraph and
         then a box. -->
    <header class="card__header impact-head" *ngIf="showHeader || headline">
      <div class="impact-head__title">
        <h2 class="card__title impact-head__h" *ngIf="showHeader">{{ headingText() }}</h2>
        <p class="impact-head__sub">{{ briefSummary() }}</p>
        <!-- §0ax. Under the subhead, not over the title: the plan is still the
             plan, and this qualifies it rather than replacing it. Rendered only
             when something is genuinely missing, so it never becomes furniture
             an operator has learned to skip. -->
        <p class="impact-head__readiness" *ngIf="readinessNote()">{{ readinessNote() }}</p>
        <!-- §0bf. Pages the latest crawl did not see as live never reach a
             package; this is where the strip says how many and why. -->
        <p class="impact-head__readiness" *ngIf="totals?.leftOutNote">{{ totals?.leftOutNote }}</p>
      </div>
      <div class="impact-head__right">
        <!-- Composed server-side (locked decision 9). Read off `headline`'s
             structured fields, not off its sentence — the sentence says the same
             two numbers in prose and would be the same content twice. -->
        <p class="package-meta" *ngIf="headline">
          <span class="stat" *ngFor="let s of headlineStats()">
            <span class="stat__k" *ngIf="s.wordFirst">{{ s.word }}</span>
            <span class="stat__n" [class.stat__n--danger]="s.danger">{{ s.value }}</span>
            <span class="stat__k" *ngIf="!s.wordFirst">{{ s.word }}</span>
          </span>
        </p>
        <!-- The primary action on the whole strip, last so it sits at the
             header's outer edge. One for the strip rather than one per package,
             because the belt is one queue: an operator works the period, and
             the packages are how it is grouped. -->
        <button
          type="button"
          class="btn btn--sm btn--accent impact-head__review"
          *ngIf="showReview && reviewablePageCount()"
          (click)="reviewAll($event)"
        >
          <!-- The belt's size, not the brief's. See `reviewablePageCount`. -->
          Review {{ reviewablePageCount() }} {{ reviewablePageCount() === 1 ? 'page' : 'pages' }} →
        </button>
      </div>
    </header>

    <table class="table table--dense impact-grid">
      <thead>
        <tr>
          <!-- Named rather than left blank: the column holds the disclosure
               caret, and an unnamed column header announces as nothing. -->
          <th class="impact-caret" aria-label="Expand"></th>
          <th class="impact-col-page">Page</th>
          <!-- The page's own target query, not its cluster. The cluster is the
               package's scope and is named once, in the header above. -->
          <th class="impact-col-keyword">Keyword</th>
          <!-- No Score column. `priorityScore` is the queue's internal ranking
               currency: it has no unit, its 0-100 range is not a percentage of
               anything, and on a live brief most rows read a flat 70 because the
               tier dominates it. A column of identical unitless numbers is a
               column an operator has to learn to ignore. The rank it encodes is
               still doing its job — it is the order the rows are in. -->
          <th class="table__num impact-col-change">{{ changeColumnLabel() }}</th>
        </tr>
      </thead>

      <!-- One tbody per package: the grouping is structural, so a screen
           reader gets it for free from the markup rather than from a colour. -->
      <tbody
        class="package-section"
        *ngFor="let pkg of packages; trackBy: trackByPackage"
        [class.package-section--bleeding]="(pkg.decayingPageCount || 0) > 0"
        [class.package-section--done]="isCompleted(pkg)"
      >
        <tr class="package-head">
          <!-- The flex row is an inner element, not the cell: a `th` with
               `display: flex` stops being a table-cell, and the browser drops
               its colspan with it. -->
          <th colspan="4" scope="colgroup" class="package-head__inner">
            <span class="package-head__row" *ngIf="packageHeading(pkg) as h">
              <!-- TOPIC / SECTION / UNASSIGNED. A cluster and a subfolder are
                   unlike things arriving as one string — "Project management &
                   delivery" and "/templates" sit adjacent and read as one kind
                   of heading in two writing styles. The qualifier says which,
                   and the name takes the matching face: a path in mono like
                   every path in the rows below, a cluster in sans. -->
              <span class="package-scope">{{ h.scopeLabel }}</span>
              <!-- Label first, action behind it: three of the four packages on
                   live data open with the decay phrase, so leading with the
                   action puts the same words atop three sections. -->
              <span class="package-name" [class.package-name--path]="h.scope === 'section'">{{
                h.name
              }}</span>
              <span class="package-kind" *ngIf="h.kind">{{ h.kind }}</span>
              <!-- The finished state, and the only new thing in this header.
                   A word rather than "18 of 18 done", which reads one character
                   away from "17 of 18 done" — the state it most needs to be
                   told apart from. The section dims around it; it does not move,
                   collapse or leave, because the strip is the record of what
                   the period held and a package that removed itself as you
                   finished it would re-rank the ones you had not. -->
              <span class="package-done" *ngIf="isCompleted(pkg)">Reviewed</span>
              <!-- Sans on the left is language, mono on the right is values. -->
              <span class="package-meta">
                <span class="stat" *ngFor="let s of stats(pkg)">
                  <span class="stat__k" *ngIf="s.wordFirst">{{ s.word }}</span>
                  <span class="stat__n" [class.stat__n--danger]="s.danger">{{ s.value }}</span>
                  <span class="stat__k" *ngIf="!s.wordFirst">{{ s.word }}</span>
                </span>
              </span>
            </span>
          </th>
        </tr>

        <!-- Link packages list the suggested links; every other kind lists
             pages. A page row is the toggle: the whole row opens the compact
             Issues peek in place. Paths only, and the path is not a link —
             the one primary action per row is the row itself, and both
             destinations live inside what it opens. -->
        <ng-container *ngIf="!isLinkPackage(pkg)">
          <ng-container *ngFor="let page of pageRows(pkg); trackBy: trackByPage">
            <tr
              class="table__row table__row--toggle package-page"
              tabindex="0"
              [class.table__row--selected]="isPageExpanded(pkg, page)"
              [attr.aria-expanded]="isPageExpanded(pkg, page)"
              (click)="togglePage(pkg, page)"
              (keydown)="onRowKeydown(pkg, page, $event)"
            >
              <td class="impact-caret" aria-hidden="true">{{ isPageExpanded(pkg, page) ? '▾' : '▸' }}</td>
              <td class="package-path" [title]="page.url">
                {{ pagePath(page) }}
                <!-- §0bf. The section header above is a claim about the package;
                     this is the page saying what it did when that claim is not
                     true of it. -->
                <span class="package-standing" *ngIf="page.standing">{{ page.standing }}</span>
              </td>
              <td
                class="package-keyword"
                [class.package-keyword--pending]="isKeywordPending(page)"
                [attr.title]="pageKeywordTitle(page)"
              >
                {{ pageKeyword(page) }}
              </td>
              <td class="table__num" [ngClass]="changeTone(page)">
                {{ changeCell(page) }}
              </td>
            </tr>

            <tr class="package-expand" *ngIf="isPageExpanded(pkg, page)">
              <td colspan="4" class="package-expand__body">
                <!-- What moved, from THIS payload — the diff layer's own
                     rendering plus the demand numbers beside it. The evidence
                     rows below answer "why"; this answers "what", and it is
                     the one thing url-detail cannot know because the period
                     comparison belongs to the brief. -->
                <!-- The period stats on the left, the way out on the right:
                     the panel's one control belongs at its top corner, not
                     buried under the evidence it opens. -->
                <div class="page-head">
                  <p class="page-why" *ngIf="pageWhy(page).length">
                    <span class="stat" *ngFor="let s of pageWhy(page)">
                      <span class="stat__k" *ngIf="s.wordFirst">{{ s.word }}</span>
                      <span class="stat__n" [class.stat__n--danger]="s.danger">{{ s.value }}</span>
                      <span class="stat__k" *ngIf="!s.wordFirst">{{ s.word }}</span>
                    </span>
                  </p>
                  <!-- Not "Expand" any more: it does not expand anything, it
                       takes you to the page's full review with the rest of the
                       queue behind it. -->
                  <button
                    type="button"
                    class="btn btn--sm page-head__expand"
                    (click)="openReview(page, $event)"
                  >
                    Review →
                  </button>
                </div>
                <!-- The compact evidence panel — enough to decide whether
                     this page is worth opening. Review opens the same
                     `app-url-detail` with the compact flag off, on its own
                     screen: one component, two depths. -->
                <app-url-detail
                  [embedUrl]="page.url"
                  embedPreset="planV2"
                  [embedActionsOnly]="true"
                  [embedCompactPeek]="true"
                  [embedPeekActions]="false"
                  [embedReviewSurface]="true"
                  [embedPlanV2Domain]="domain"
                ></app-url-detail>


              </td>
            </tr>
          </ng-container>
        </ng-container>

        <!-- A link package collapses to this one row (B6). Fifteen rows of
             source → target → anchor, twice over, is what made the first screen
             read as a link inventory with some page work attached: thirty rows
             of anchors against twelve rows of pages.

             The row IS the disclosure, so nothing is lost — the anchors are one
             click away and already in the payload, which is why this toggles
             locally instead of asking the host for anything. -->
        <ng-container *ngIf="isLinkPackage(pkg)">
          <tr
            class="table__row table__row--toggle package-link-summary"
            tabindex="0"
            [class.table__row--selected]="isLinksExpanded(pkg)"
            [attr.aria-expanded]="isLinksExpanded(pkg)"
            (click)="toggleLinks(pkg, $event)"
            (keydown)="onLinksKeydown(pkg, $event)"
          >
            <td class="impact-caret" aria-hidden="true">{{ isLinksExpanded(pkg) ? '▾' : '▸' }}</td>
            <td class="package-path" colspan="2">{{ linkRowSummary(pkg) }}</td>
            <td class="table__num">—</td>
          </tr>

          <!-- One row per SOURCE page, its links beneath it (§0az). Every
               other row on this surface is a page, and the work is one visit
               per page: a flat list of pairs put the same page on seven rows
               and left the operator to batch them by hand. The page row
               carries the count; a link line carries only what differs — the
               target and the anchor — because the source is the row above.

               Neither toggles: there is no peek for a pair, and the page row
               is a heading for its links, not a review surface. -->
          <ng-container *ngIf="isLinksExpanded(pkg)">
            <ng-container *ngFor="let page of linkPages(pkg); trackBy: trackByLinkPage">
              <!-- One link: the heading row IS the link row. Two rows to say
                   "this page, that link" is the height this grouping was
                   supposed to save. -->
              <tr class="package-link-page package-link-page--inline" *ngIf="page.inline">
                <td class="impact-caret" aria-hidden="true"></td>
                <td class="package-path" [title]="page.sourceUrl">
                  {{ page.sourcePath }}
                  <span class="link-arrow" aria-hidden="true">→</span>
                  <span class="link-target" [title]="page.links[0].targetUrl">{{ page.links[0].targetPath }}</span>
                </td>
                <td class="package-keyword">“{{ page.links[0].suggestedAnchor }}”</td>
                <td class="table__num">1 link</td>
              </tr>

              <tr class="package-link-page" *ngIf="!page.inline">
                <td class="impact-caret" aria-hidden="true"></td>
                <td class="package-path" colspan="2" [title]="page.sourceUrl">{{ page.sourcePath }}</td>
                <td class="table__num">{{ page.links.length }} links</td>
              </tr>
              <tr class="package-link" *ngFor="let link of page.inline ? [] : page.links; trackBy: trackByLink">
                <td class="impact-caret" aria-hidden="true"></td>
                <td class="package-path package-link__target">
                  <span class="link-arrow" aria-hidden="true">→</span>
                  <span class="link-target" [title]="link.targetUrl">{{ link.targetPath }}</span>
                </td>
                <!-- The anchor text is the value on a link row, and it is language,
                     so it takes the Keyword column rather than a column of its own. -->
                <td class="package-keyword">“{{ link.suggestedAnchor }}”</td>
                <td class="table__num"></td>
              </tr>
            </ng-container>
          </ng-container>
        </ng-container>

        <!-- The package's own last row: what the cap left off on the left, and
             where the rest of it lives on the right. One action per package,
             and it is the continuation of the sentence beside it rather than a
             fourth identical call to action. -->
        <tr class="package-foot" *ngIf="moreLabel(pkg) || showOpenInIssues">
          <td colspan="2" class="package-more">
            <!-- A count that says thirteen pages exist and will not show them
                 is a dead end. Only a page package can expand: a link package's
                 `pageCount` counts its SOURCE pages, so "+N more" there is about
                 links, and `moreLabel` already branches on that. -->
            <button
              type="button"
              class="btn btn--sm btn--bare package-more__btn"
              *ngIf="canExpand(pkg); else moreText"
              [disabled]="isExpanding(pkg)"
              (click)="onExpandPackage(pkg, $event)"
            >
              {{ isExpanding(pkg) ? 'Loading the rest…' : moreLabel(pkg) }}
            </button>
            <ng-template #moreText>{{ moreLabel(pkg) }}</ng-template>
          </td>
          <td colspan="2" class="package-foot__link">
            <a
              class="package-open"
              *ngIf="showOpenInIssues"
              routerLink="/inventory"
              [queryParams]="{ section: 'planV2' }"
              >Open in Issues →</a
            >
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <ng-template #noPackages>
    <p class="package-empty">{{ emptyText }}</p>
    <!-- An empty strip is exactly where "the data is still arriving" is most
         load-bearing: without it, "nothing to work on" and "nothing has been
         worked out yet" read identically. -->
    <p class="package-empty package-empty__readiness" *ngIf="readinessNote()">
      {{ readinessNote() }}
    </p>
  </ng-template>
</section>
